/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  DELETE_IMAGE_FROM_CATEGORY_SUCCESS,
  COPY_IMAGES_TO_TEAMS_SUCCESS,
  COPY_IMAGES_TO_TEAMS_FAILURE,
  COPY_IMAGES_TO_TEAMS_START,
  FETCH_USER_IMAGES_SUCCESS,
  FETCH_USER_IMAGES_FAILURE,
  FETCH_USER_IMAGES_START,
  UPLOAD_IMAGES_SUCCESS,
  UPLOAD_IMAGES_FAILURE,
  DELETE_IMAGE_FAILURE,
  UPDATE_UPLOAD_QUEUE,
  UPLOAD_IMAGES_START,
  RESET_MANAGER_STATE,
  DELETE_IMAGE_START,
  REPORT_ERROR,
  CLEAR_ERROR
} from '../actions/types';

const defaultState = {
  loadingUserImages: false,
  userImages: {
    personal: [],
    team: [],
  },
  uploadQueue: {
    uploading: [],
    uploaded: [],
    imageLinks: [],
  },
  deleteQueue: {
    activeDeletions: [],
    isDeleting: false,
  },
  pendingUploads: 0,
  uploadComplete: true,
  errors: [],
};

export default function imageManagerReducers(state = defaultState, action) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_USER_IMAGES_START:
    case FETCH_USER_IMAGES_SUCCESS:
    case COPY_IMAGES_TO_TEAMS_START:
    case COPY_IMAGES_TO_TEAMS_FAILURE: {
      return {
        ...state,
        ...payload,
      };
    }
    case FETCH_USER_IMAGES_FAILURE: {
      const { loadingUserImages } = payload;
      const { error } = payload;
      let { errors } = state;

      errors = produce(errors, (draft) => {
        draft.push(error);
      });

      return {
        ...state,
        loadingUserImages,
        errors,
      };
    }

    case UPDATE_UPLOAD_QUEUE: {
      let { uploadQueue } = state;
      const { newImages, isLink } = payload;

      // Update the uploading queue with new images and update the link preview for links;
      uploadQueue = produce(uploadQueue, (draft) => {
        if (!isLink) {
          draft.uploading = draft.uploading.concat(newImages);
        } else {
          for (let i = 0; i < newImages.length; i += 1) {
            const containsLink = Boolean(draft.imageLinks.find((image) => image.url === newImages[i].url));
            if (containsLink) {
              return;
            }
            draft.imageLinks.push(newImages[i]);
          }
        }
      });

      return {
        ...state,
        uploadQueue,
      };
    }

    case UPLOAD_IMAGES_START: {
      const { pendingUploads } = state;
      const { additionalUploads } = payload;

      return {
        ...state,
        pendingUploads: pendingUploads + additionalUploads,
        uploadComplete: false,
      };
    }

    case COPY_IMAGES_TO_TEAMS_SUCCESS: {
      const { imageId } = payload;
      let { userImages } = state;

      const isInTeams = userImages.team.find((image) => image.id === imageId);

      // Updates the local teams list
      if (!isInTeams) {
        userImages = produce(userImages, (draft) => {
          const newTeamImage = draft.personal.find((image) => image.id === imageId);
          draft.team.unshift(newTeamImage);
        });
      }

      return {
        ...state,
        userImages,
      };
    }

    case UPLOAD_IMAGES_SUCCESS: {
      const { image, imageType, imageId } = payload;
      const { pendingUploads } = state;
      let { uploadQueue, userImages } = state;

      // Remove transition the image from the uploading queue to the uploaded set
      uploadQueue = produce(uploadQueue, (draft) => {
        draft.uploading = draft.uploading.filter((uploadingImage) => uploadingImage.id !== imageId);
        draft.uploaded.push(image);
      });

      // Add the image to the appropriate user image category
      userImages = produce(userImages, (draft) => {
        draft[imageType].unshift(image);
      });

      return {
        ...state,
        userImages,
        pendingUploads: pendingUploads && pendingUploads - 1,
        uploadQueue,
        uploadComplete: pendingUploads <= 1,
      };
    }

    case UPLOAD_IMAGES_FAILURE: {
      const { pendingUploads } = state;
      const { imageId, newError } = payload;

      let { uploadQueue, errors } = state;

      // Remove the image from the uploading queue
      uploadQueue = produce(uploadQueue, (draft) => {
        draft.uploading = draft.uploading.filter((uploadingImage) => uploadingImage.id !== imageId);
      });
      errors = produce(errors, (draft) => {
        draft.push(newError);
      });

      return {
        ...state,
        pendingUploads: pendingUploads && pendingUploads - 1,
        uploadQueue,
        uploadComplete: pendingUploads <= 1,
        errors,
      };
    }

    case DELETE_IMAGE_START: {
      const { imageIds, all, type: category } = payload;
      let { userImages, deleteQueue } = state;

      deleteQueue = produce(deleteQueue, (draft) => {
        if (all) {
          draft.activeDeletions = userImages[category].map((image) => image.id);
        } else if (!all && imageIds.length > 0) {
          draft.activeDeletions = [...imageIds];
        } else {
          draft.activeDeletions.push(imageIds);
        }
        draft.isDeleting = true;
      });

      return {
        ...state,
        deleteQueue,
      };
    }

    case DELETE_IMAGE_FROM_CATEGORY_SUCCESS: {
      const { imageIds, all, type: category } = payload;
      let { userImages, deleteQueue, uploadQueue } = state;

      // Remove deleted images from user galleries
      userImages = produce(userImages, (draft) => {
        if (all) {
          draft[category] = [];
        } else if (!all && imageIds.length > 0) {
          draft[category] = draft[category].filter((image) => imageIds.indexOf(image.id) === -1);
        } else {
          draft[category] = draft[category].filter((image) => image.id !== imageIds);
        }
      });

      // Remove deleted images from deleteQueue
      deleteQueue = produce(deleteQueue, (draft) => {
        draft.activeDeletions = [];
        draft.isDeleting = false;
      });

      // Remove deleted images from uploadQueue
      uploadQueue = produce(uploadQueue, (draft) => {
        if (draft.uploaded.length) {
          if (imageIds.length > 0) {
            draft.uploaded = draft.uploaded.filter((image) => imageIds.indexOf(image.id) === -1);
          } else {
            draft.uploaded = draft.uploaded.filter((image) => image.id !== imageIds);
          }
        }
      });

      return {
        ...state,
        userImages,
        deleteQueue,
        uploadQueue,
      };
    }

    case DELETE_IMAGE_FAILURE: {
      let { deleteQueue } = state;

      deleteQueue = produce(deleteQueue, (draft) => {
        draft.activeDeletions = [];
        draft.isDeleting = false;
      });

      return {
        ...state,
        deleteQueue,
      };
    }

    case REPORT_ERROR: {
      const { error } = payload;
      let { errors } = state;
      errors = produce(errors, (draft) => {
        draft.push(error);
      });

      return {
        ...state,
        errors,
      };
    }
    case CLEAR_ERROR: {
      let { errors } = state;

      errors = produce(errors, (draft) => {
        draft.length = 0;
      });

      return {
        ...state,
        errors,
      };
    }
    case RESET_MANAGER_STATE: {
      return {
        ...state,
        uploadQueue: {
          uploading: [],
          uploaded: [],
          imageLinks: [],
        },
        pendingUploads: 0,
        uploadComplete: true,
        deletingImages: false,
        errors: [],
      };
    }

    default: {
      return state;
    }
  }
}
